import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import {
  Grid,
  TextField,
  Button,
  CardMedia,
  Input,
  IconButton,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  Chip,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { GridCloseIcon } from "@mui/x-data-grid";
import { showError, showToast } from "../../constants/toasts";
import { useLazyGetCategoryQuery } from "../../services/categories";
import { useLazyGetFacilitiesQuery } from "../../services/facility";
import { useFormik } from "formik";
import {
  useAddVenueMutation,
  useLazyGetVenueDetailsQuery,
  useUpdateVenueStorageMutation,
} from "../../services/venue";
import { UploadMedia } from "../../utils/mediaUpload";
import * as Yup from "yup";
import { VenueType } from "../../types/General";
import { isNumber } from "../../utils/validations";
import CustomToggle from "../../helpers/CustomToggle";

const AddVenueStorageOwner = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const isVenue = type === "Venue";

  const [fetchCategories] = useLazyGetCategoryQuery();
  const [fetchFacilities] = useLazyGetFacilitiesQuery();
  const [venueDetail] = useLazyGetVenueDetailsQuery();
  const [updateVenue] = useUpdateVenueStorageMutation();
  const [addVenue] = useAddVenueMutation();
  const [images, setImages] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [facilitiesList, setFacilitiesList] = useState<any[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [venueData, setVenueData] = useState<VenueType>();

  const handleCategoryChange = (event: any, newValue: any[]) => {
    const selectedIds = (newValue || []).map((item: any) => item?._id);
    setSelectedCategory(selectedIds);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      const invalidFiles = Array.from(files).filter(
        (file: any) => !allowedTypes.includes(file.type)
      );
      if (invalidFiles.length > 0) {
        showError("Invalid image please upload again");
        return;
      }
      if (images?.length + files.length > 5) {
        showError("Maximum 5 images allowed");
        return;
      }
      const uploadedImages = Array.from(files).map((file: any) => ({
        file,
        url: URL.createObjectURL(file),
      }));
      setImages((prevImages: any) => [...prevImages, ...uploadedImages]);
    }
  };

  const getCategories = async (sourceType?: string) => {
    try {
      const response = await fetchCategories({
        page: 1,
        limit: 100,
        search: "",
        categoryType: sourceType || (isVenue ? "VENUE" : "STORAGE"),
      }).unwrap();
      if (response?.statusCode === 200) {
        setCategoriesList((response?.data?.data || []) as any);
      } else {
        setCategoriesList([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getVenueDetailApi = async () => {
    try {
      const response = await venueDetail({
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        setVenueData(response?.data);
        setImages(response?.data?.images);
        const categoryField = (response?.data as any)?.categoryId;
        if (Array.isArray(categoryField)) {
          setSelectedCategory(
            categoryField.map((cat: any) => (typeof cat === "string" ? cat : cat?._id)).filter(Boolean)
          );
        } else if (categoryField?._id) {
          setSelectedCategory([categoryField._id]);
        } else if (typeof categoryField === "string") {
          setSelectedCategory([categoryField]);
        } else {
          setSelectedCategory([]);
        }
        setSelectedFacilities(
          response?.data?.facilityId?.map((facility: any) => facility._id) || []
        );
      } else {
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getFacilities = async (sourceType?: string) => {
    try {
      const response = await fetchFacilities({
        page: 1,
        limit: 100,
        search: "",
        type: sourceType || (isVenue ? "VENUE" : "STORAGE"),
      }).unwrap();
      if (response?.statusCode === 200) {
        setFacilitiesList((response?.data?.data || []) as any);
      } else {
        setFacilitiesList([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      venueName: venueData?.name || "",
      location: venueData?.address || "",
      guests: venueData?.guestAllowed || "",
      description: venueData?.description || "",
      resourcesInclude: venueData?.resourcesIncluded || "",
      safetyParameters: venueData?.safetyParameters || "",
      aboutVenue: venueData?.aboutVenue || "",
      cancellationPolicy: venueData?.cancellationPolicy || "",
      pricePerDay: venueData?.setPricing?.perDay || "",
      pricePerHour: venueData?.setPricing?.perHour || "",
      pricePerPerson: venueData?.setPricing?.perPerson || "",
      cleaningFees: venueData?.cleaningFee || "",
      insuranceAmount: venueData?.insuranceFee || "",
      cateringAmount: venueData?.cateringFee || "",
      decorationAmount: venueData?.decorationFee || "",
      securityAmount: venueData?.securityFee || "",
      otherPolicy: venueData?.otherPolicy || "",
      isRecommended: venueData?.isRecommended || false,
    },
    validationSchema: Yup.object({
      venueName: Yup.string().required(
        `${isVenue ? "Venue" : "Storage"} Name is required`
      ),
      location: Yup.string().required("Location is required"),
      guests: Yup.number().required(
        isVenue ? "No of guests is required" : "Capacity is required"
      ),
      description: Yup.string().required("Description is required"),
      resourcesInclude: Yup.string().required("Resources Include is required"),
      safetyParameters: Yup.string().required("Safety Parameters are required"),
      aboutVenue: Yup.string().required(
        `About ${isVenue ? "Venue" : "Storage"} is required`
      ),
      cancellationPolicy: Yup.string().required(
        "Cancellation Policy is required"
      ),
      // otherPolicy: Yup.string().required("Other Policy is required"),
      pricePerDay: Yup.string().required("Price per day is required"),
      pricePerHour: Yup.string().required("Price per hour is required"),
      pricePerPerson: Yup.string().required(
        `Price per ${isVenue ? "person" : "month"} is required`
      ),
      cleaningFees: Yup.string().required("Cleaning fees is required"),
      insuranceAmount: Yup.string().required("Insurance amount is required"),
      cateringAmount: Yup.string().required(
        isVenue
          ? "Catering amount is required"
          : "Packing Assistance is required"
      ),
      decorationAmount: Yup.string().required(
        isVenue ? "Decoration amount is required" : "Transportation is required"
      ),
      securityAmount: Yup.string().required("Security amount is required"),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);
      let pictures: any[] = [];
      if (images?.length) {
        await Promise.all(
          images?.map(async (item: any) => {
            if (!item?.url) {
              pictures?.push(item);
            } else {
              const res = await UploadMedia(item?.file);
              if (res?.statusCode === 200) {
                pictures?.push(res.data);
              }
            }
          })
        );
      }

      if (!id && !images?.length) {
        showError("Please upload images");
        return;
      }

      const body = {
        ...(id ? {} : { type: isVenue ? "VENUE" : "STORAGE" }),
        categoryId: selectedCategory,
        isRecommended: values?.isRecommended,
        name: values.venueName,
        images: pictures,
        latitude: 0,
        longitude: 0,
        description: values.description,
        facilityId: selectedFacilities,
        resourcesIncluded: values?.resourcesInclude,
        safetyParameters: values.safetyParameters,
        guestAllowed: values.guests,
        address: values.location,
        aboutVenue: values.aboutVenue,
        cancellationPolicy: values.cancellationPolicy,
        otherPolicy: values.otherPolicy,
        securityFee: values.cleaningFees,
        cleaningFee: values.cleaningFees,
        insuranceFee: values.insuranceAmount,
        cateringFee: values.cateringAmount,
        decorationFee: values.decorationAmount,
        setPricing: {
          perHour: values.pricePerHour,
          perDay: values.pricePerDay,
          ...(!isVenue
            ? { perMonth: values?.pricePerPerson }
            : { perPerson: values?.pricePerPerson }),
        },
        ...(!isVenue
          ? {
              packingAssistanceFee: values?.cateringAmount,
              transportationFee: values?.decorationAmount,
              capacity: values?.guests,
            }
          : {
              cateringFee: values?.cateringAmount,
              decorationFee: values?.decorationAmount,
              guestAllowed: values?.guests,
            }),
      };

      if (id) {
        try {
          const response = await updateVenue({ id, body }).unwrap();
          if (response?.statusCode === 200) {
            showToast(response?.message || "");
            navigate("/manage-venue-owners");
          } else {
          }
        } catch (error: any) {
          showError(error?.data?.message || "");
        }
      } else {
        try {
          const response = await addVenue(body).unwrap();
          if (response?.statusCode === 200) {
            showToast(response?.message || "");
            navigate("/manage-venue-owners");
          } else {
          }
        } catch (error: any) {
          showError(error?.data?.message || "");
        }
      }
    },
  });

  useEffect(() => {
    if (id) {
      getVenueDetailApi();
    } else {
      getCategories();
      getFacilities();
    }
  }, []);

  useEffect(() => {
    if (id && venueData?.type) {
      const recordType = venueData?.type; // "VENUE" | "STORAGE"
      getCategories(recordType);
      getFacilities(recordType);
    }
  }, [id, venueData?.type]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{`Add ${isVenue ? "Venue" : "Storage"}`}</h1>
        <p>
          <span
            onClick={() => navigate("/dashboard")}
            style={{ cursor: "pointer" }}
          >
            Dashboard
          </span>{" "}
          -{" "}
          <span
            onClick={() => navigate("/manage-venue-owners")}
            style={{ cursor: "pointer" }}
          >
            {`Manage ${isVenue ? "Venue" : "Storage"}`}
          </span>{" "}
          - Add
        </p>
      </div>
      <div className="cards">
        <div className="form">
          <form onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label>Upload Images</label>
                {images?.length >= 5 ? null : (
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      flexWrap: "nowrap",
                      marginTop: 8,
                      marginBottom: 20,
                      // overflowX: "auto",
                      alignItems: "center",
                    }}
                  >
                    <label
                      className="upload_image no_radius v2"
                      htmlFor="icon-button-file"
                      style={{
                        cursor: "pointer",
                        width: 300,
                        height: 150,
                        minWidth: 120,
                        minHeight: 120,
                        border: "1.5px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f5f5f5",
                        flex: "0 0 auto",
                      }}
                    >
                      <Input
                        sx={{ display: "none" }}
                        id="icon-button-file"
                        type="file"
                        inputProps={{
                          accept: "image/png,image/jpeg",
                          multiple: true,
                        }}
                        onChange={handleFileUpload}
                      />
                      <span
                        className="upload_image_holder"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src="/static/images/placeholder.png"
                          alt="icon"
                          style={{ width: 40, height: 40, opacity: 0.5 }}
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="icon"
                            style={{ width: 24, height: 24 }}
                          />
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {images?.map((img: any, idx) => (
                  <div
                    key={idx}
                    className="upload_image no_radius v2"
                    style={{
                      position: "relative",
                      width: 200,
                      height: 100,
                      minWidth: 120,
                      minHeight: 120,
                      border: "1.5px solid #ccc",

                      overflow: "visible",
                      background: "#fff",
                      flex: "0 0 auto",
                      marginRight: 20,
                    }}
                  >
                    <IconButton
                      size="small"
                      style={{
                        position: "absolute",
                        top: -10,
                        right: -10,
                        background: "#e71c57",
                        color: "#fff",
                        zIndex: 2,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() =>
                        setImages(images?.filter((_, i) => i !== idx))
                      }
                    >
                      <GridCloseIcon fontSize="small" />
                    </IconButton>
                    <div
                      className="upload_image_holder"
                      style={{ width: "100%", height: "100%" }}
                    >
                      <CardMedia
                        component="img"
                        image={img?.url || img}
                        alt={""}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="control_group w_50">
                <label>{`${isVenue ? "Venue" : "Storage"} Name`}</label>
                <TextField
                  placeholder={`${isVenue ? "Venue" : "Storage"} Name`}
                  hiddenLabel
                  fullWidth
                  className="text_field"
                  name="venueName"
                  id="venueName"
                  value={formik.values.venueName}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 50,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.venueName && formik.errors.venueName
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>Category</label>
                <Autocomplete
                  className="users_autocomplete"
                  multiple
                  options={categoriesList}
                  disableCloseOnSelect
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={categoriesList.filter((category: any) =>
                    selectedCategory.includes(category._id)
                  )}
                  onChange={handleCategoryChange}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?._id === value?._id
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option?.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Category"
                      fullWidth
                      className="text_field"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index: number) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option._id}
                      />
                    ))
                  }
                  clearOnBlur={false}
                  clearIcon
                />
                {error && !selectedCategory?.length ? (
                  <p className="err_msg">Category is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_50">
                <label>Location</label>
                <TextField
                  placeholder="Enter Loacation"
                  hiddenLabel
                  fullWidth
                  name="location"
                  id="location"
                  className="text_field"
                  value={formik.values.location}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.location && formik.errors.location}
                />
              </div>

              <div className="control_group w_50">
                <label>Facilities</label>
                <Autocomplete
                  className="users_autocomplete"
                  multiple
                  options={facilitiesList}
                  disableCloseOnSelect
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={facilitiesList.filter((facility: any) =>
                    selectedFacilities.includes(facility._id)
                  )}
                  onChange={(event, newValue) => {
                    const selectedIds = newValue.map((item: any) => item._id);
                    setSelectedFacilities(selectedIds);
                  }}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?._id === value?._id
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option?.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Facilities"
                      fullWidth
                      className="text_field"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option._id}
                      />
                    ))
                  }
                  clearOnBlur={false}
                  clearIcon
                />
                {!selectedFacilities?.length && error ? (
                  <h6 className="err_msg">This field is required</h6>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_50">
                <label>{isVenue ? "No of guests" : "Capacity (sq. ft.)"}</label>
                <TextField
                  placeholder={isVenue ? "No of guests" : "Capacity (sq. ft.)"}
                  hiddenLabel
                  fullWidth
                  name="guests"
                  id="guests"
                  className="text_field"
                  value={formik.values.guests}
                  onChange={(val) => {
                    if (
                      val.target.value === " " ||
                      val.target.value === "." ||
                      !isNumber(val.target.value)
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 7,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched.guests && formik.errors.guests}
                />
              </div>
              <div
                style={{
                  marginTop: 30,
                }}
                className="control_group w_50"
              >
                <label>Recommended </label>
                <CustomToggle
                  checked={formik?.values.isRecommended}
                  onChange={() => {
                    formik.setFieldValue(
                      "isRecommended",
                      !formik.values.isRecommended
                    );
                  }}
                  id={"isRecommended"} // size="small"
                />
              </div>
              <div className="control_group w_50">
                <label>Description</label>
                <TextField
                  placeholder="Description"
                  hiddenLabel
                  fullWidth
                  multiline
                  rows={4}
                  name="description"
                  id="description"
                  className="text_field"
                  value={formik.values.description}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 300,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </div>
              <div className="control_group w_50">
                <label>Resources Includes</label>
                <TextField
                  placeholder="Resources Include"
                  hiddenLabel
                  fullWidth
                  multiline
                  rows={4}
                  name="resourcesInclude"
                  id="resourcesInclude"
                  className="text_field"
                  value={formik.values.resourcesInclude}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 300,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.resourcesInclude &&
                    formik.errors.resourcesInclude
                  }
                />
              </div>
              <div className="control_group w_50">
                <label>Safety Parameters</label>
                <TextField
                  placeholder="Safety Parameters"
                  hiddenLabel
                  fullWidth
                  multiline
                  rows={4}
                  name="safetyParameters"
                  id="safetyParameters"
                  className="text_field"
                  value={formik.values.safetyParameters}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 300,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.safetyParameters &&
                    formik.errors.safetyParameters
                  }
                />
              </div>
              <div className="control_group w_50">
                <label>{`About ${isVenue ? "Venue" : "Storage"}`}</label>
                <TextField
                  placeholder={`About ${isVenue ? "Venue" : "Storage"}`}
                  hiddenLabel
                  fullWidth
                  multiline
                  rows={4}
                  name="aboutVenue"
                  id="aboutVenue"
                  className="text_field"
                  value={formik.values.aboutVenue}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 300,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.aboutVenue && formik.errors.aboutVenue
                  }
                />
              </div>
              <div className="control_group w_50">
                <label>Cancellation Policy</label>
                <TextField
                  placeholder="Cancellation Policy"
                  hiddenLabel
                  fullWidth
                  multiline
                  rows={4}
                  name="cancellationPolicy"
                  id="cancellationPolicy"
                  className="text_field"
                  value={formik.values.cancellationPolicy}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 300,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.cancellationPolicy &&
                    formik.errors.cancellationPolicy
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>Other Policy</label>
                <TextField
                  placeholder="Other Policy"
                  hiddenLabel
                  fullWidth
                  multiline
                  rows={4}
                  name="otherPolicy"
                  id="otherPolicy"
                  className="text_field"
                  value={formik.values.otherPolicy}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 300,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.otherPolicy && formik.errors.otherPolicy
                  }
                />
              </div>

              <h3>Set Pricing</h3>

              <div className="control_group w_50">
                <label>Price per day</label>
                <TextField
                  placeholder="Price per day"
                  hiddenLabel
                  fullWidth
                  name="pricePerDay"
                  id="pricePerDay"
                  className="text_field"
                  value={formik.values.pricePerDay}
                  onChange={(val) => {
                    if (
                      val.target.value === " " ||
                      val.target.value === "." ||
                      !isNumber(val.target.value)
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.pricePerDay && formik.errors.pricePerDay
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>Price per hour</label>
                <TextField
                  placeholder="Price per hour"
                  hiddenLabel
                  fullWidth
                  name="pricePerHour"
                  id="pricePerHour"
                  className="text_field"
                  value={formik.values.pricePerHour}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.pricePerHour && formik.errors.pricePerHour
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>{`Price per ${isVenue ? "person" : "month"}`}</label>
                <TextField
                  placeholder={`Price per ${isVenue ? "person" : "month"}`}
                  hiddenLabel
                  fullWidth
                  name="pricePerPerson"
                  id="pricePerPerson"
                  className="text_field"
                  value={formik.values.pricePerPerson}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.pricePerPerson &&
                    formik.errors.pricePerPerson
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>Cleaning fees</label>
                <TextField
                  placeholder="Cleaning Fees"
                  hiddenLabel
                  fullWidth
                  name="cleaningFees"
                  id="cleaningFees"
                  className="text_field"
                  value={formik.values.cleaningFees}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.cleaningFees && formik.errors.cleaningFees
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>Insurance amount</label>
                <TextField
                  placeholder="Insurance amount"
                  hiddenLabel
                  fullWidth
                  name="insuranceAmount"
                  id="insuranceAmount"
                  className="text_field"
                  value={formik.values.insuranceAmount}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.insuranceAmount &&
                    formik.errors.insuranceAmount
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>
                  {isVenue ? "Catering amount" : "Packing Assistance"}
                </label>
                <TextField
                  placeholder={
                    isVenue ? "Catering amount" : "Packing Assistance"
                  }
                  hiddenLabel
                  fullWidth
                  name="cateringAmount"
                  id="cateringAmount"
                  className="text_field"
                  value={formik.values.cateringAmount}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.cateringAmount &&
                    formik.errors.cateringAmount
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>
                  {isVenue ? "Decoration amount" : "Transportation"}
                </label>
                <TextField
                  placeholder={isVenue ? "Decoration amoun" : "Transportation"}
                  hiddenLabel
                  fullWidth
                  name="decorationAmount"
                  id="decorationAmount"
                  className="text_field"
                  value={formik.values.decorationAmount}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.decorationAmount &&
                    formik.errors.decorationAmount
                  }
                />
              </div>

              <div className="control_group w_50">
                <label>Security amount</label>
                <TextField
                  placeholder="Security amount"
                  hiddenLabel
                  fullWidth
                  name="securityAmount"
                  id="securityAmount"
                  className="text_field"
                  value={formik.values.securityAmount}
                  onChange={(val) => {
                    if (
                      (val.target.value === " " || val.target.value === ".",
                      !isNumber(val.target.value))
                    ) {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{
                    maxLength: 8,
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.securityAmount &&
                    formik.errors.securityAmount
                  }
                />
              </div>
            </div>
            <div className="form_btn">
              <Button
                className="btn btn_primary"
                type="submit"
                onClick={() => setError(true)}
              >
                Save
              </Button>
              <Button
                className="btn btn_secondary"
                onClick={() => navigate("/manage-venue-owners")}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </MainContainer>
  );
};

export default AddVenueStorageOwner;
